import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { clearAuthState, logout } from '@/shared/auth/auth.reducer';
import { readPagedRows } from '@/shared/utils/spring-api';
import { getBranches } from './branch.reducer';
import { getCaseCategories } from './caseCategory.reducer';
import { getDepartments } from './department.reducer';
import { getDistricts } from './district.reducer';
import { getPermissions, getPublicUserPermissions } from './permission.reducer';
import { getProvinces } from './province.reducer';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
import { getRoles } from './role.reducer';
import { getUsers } from './user.reducer';
import { getVillages } from './village.reducer';
import { API_ROUTES } from '@/config/api-routes';
import axios from 'axios';
/** Large page size for dropdown / lookup catalogues loaded once after sign-in. */
export const REFERENCE_CATALOG_QUERY = {
    page: 0,
    size: 1000,
    sort: 'id,asc',
};
export const PROVINCE_CATALOG_QUERY = {
    page: 0,
    size: 40,
    sort: 'id,asc',
};
export const DISTRICT_CATALOG_QUERY = {
    page: 0,
    size: 1000,
    sort: 'id,asc',
};
const emptyCatalog = {
    provinces: [],
    districts: [],
    villages: [],
    branches: [],
    departments: [],
    roles: [],
    permissions: [],
    caseCategories: [],
    users: [],
};
const initialState = {
    bootstrapping: false,
    bootstrapped: false,
    errorMessage: null,
    issues: [],
    catalog: emptyCatalog,
};
function snapshotCatalogFromState(state) {
    return {
        provinces: [...state.province.provinces],
        districts: [...state.district.districts],
        villages: [...state.village.villages],
        branches: [...state.branch.branches],
        departments: [...state.department.departments],
        roles: [...state.role.roles],
        permissions: [...state.permission.permissions],
        caseCategories: [...state.caseCategory.caseCategories],
        users: [...state.user.users],
    };
}
async function fetchIssueCatalog() {
    const res = await axios.get(`${API_ROUTES.issues}?page=0&size=1000&sort=id,asc`);
    const { rows } = readPagedRows(res.data, res.headers);
    return rows.map((i) => ({
        id: String(i.id ?? ''),
        name: i.name ?? '',
    }));
}
function departmentLabel(d) {
    return d.departmentName ?? d.name ?? '';
}
/** Build case-panel reference dropdowns from the session catalogue cache. */
export function buildCasePanelReferenceFromState(state) {
    const { catalog, issues } = state.referenceData;
    return {
        issues,
        provinces: catalog.provinces.map((p) => ({
            id: String(p.id ?? ''),
            name: p.name ?? '',
        })),
        districts: catalog.districts.map((d) => ({
            id: String(d.id ?? ''),
            name: d.name ?? '',
            provinceId: d.provinceId != null
                ? String(d.provinceId)
                : d.province?.id != null
                    ? String(d.province.id)
                    : undefined,
        })),
        branches: catalog.branches.map((b) => ({
            id: String(b.id ?? ''),
            branchName: b.branchName ?? b.name ?? '',
        })),
        departments: catalog.departments.map((d) => ({
            id: String(d.id ?? ''),
            name: departmentLabel(d),
        })),
        agents: catalog.users.map((u) => ({
            id: String(u.id ?? u.login ?? ''),
            name: u.fullName ?? u.login ?? '',
        })),
        callerTitles: [],
        socialMedias: [],
        priorities: [
            { label: 'HIGH', value: 'HIGH' },
            { label: 'MEDIUM', value: 'MEDIUM' },
            { label: 'LOW', value: 'LOW' },
        ],
    };
}
export const bootstrapReferenceData = createAsyncThunk('referenceData/bootstrap', async (_, { dispatch, getState }) => {
    const state = getState();
    if (state.referenceData.bootstrapped) {
        return {
            issues: state.referenceData.issues,
            catalog: state.referenceData.catalog,
            skipped: true,
        };
    }
    const catalogQuery = REFERENCE_CATALOG_QUERY;
    await Promise.allSettled([
        dispatch(getProvinces(PROVINCE_CATALOG_QUERY)).unwrap(),
        dispatch(getDistricts(DISTRICT_CATALOG_QUERY)).unwrap(),
        dispatch(getVillages()).unwrap(),
        dispatch(getBranches(catalogQuery)).unwrap(),
        dispatch(getDepartments(catalogQuery)).unwrap(),
        dispatch(getRoles(catalogQuery)).unwrap(),
        dispatch(getPermissions(catalogQuery)).unwrap(),
        dispatch(getCaseCategories(catalogQuery)).unwrap(),
        dispatch(getUsers(catalogQuery)).unwrap(),
        dispatch(getPublicUserPermissions()).unwrap(),
    ]);
    const issues = await fetchIssueCatalog();
    const catalog = snapshotCatalogFromState(getState());
    return { issues, catalog, skipped: false };
}, {
    serializeError: serializeAxiosError,
    condition: (_, { getState }) => {
        const state = getState();
        return !state.referenceData.bootstrapped && !state.referenceData.bootstrapping;
    },
});
const BOOTSTRAP_WAIT_MS = 50;
const BOOTSTRAP_WAIT_MAX = 120;
/** Ensures post-login catalogues are in Redux (no-op when already loaded). */
export async function ensureReferenceDataBootstrapped(dispatch, getState) {
    if (getState().referenceData.bootstrapped) {
        return;
    }
    await dispatch(bootstrapReferenceData());
    let waits = 0;
    while (getState().referenceData.bootstrapping && waits < BOOTSTRAP_WAIT_MAX) {
        await new Promise((resolve) => setTimeout(resolve, BOOTSTRAP_WAIT_MS));
        waits += 1;
    }
}
export const ReferenceDataSlice = createSlice({
    name: 'referenceData',
    initialState,
    reducers: {
        resetReferenceData() {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(bootstrapReferenceData.pending, (state) => {
            state.bootstrapping = true;
            state.errorMessage = null;
        })
            .addCase(bootstrapReferenceData.fulfilled, (state, action) => {
            state.bootstrapping = false;
            state.bootstrapped = true;
            if (!action.payload.skipped) {
                state.issues = action.payload.issues;
                state.catalog = action.payload.catalog;
            }
        })
            .addCase(bootstrapReferenceData.rejected, (state, action) => {
            state.bootstrapping = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addCase(logout.fulfilled, () => initialState)
            .addCase(clearAuthState, () => initialState);
    },
});
export const { resetReferenceData } = ReferenceDataSlice.actions;
export default ReferenceDataSlice.reducer;
